import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sparkles, X, ArrowRight, Star } from "lucide-react";
import { isAndroidTWA } from "@/utils/runtimeContext";

const DISMISS_KEY = "vitrine-banner-dismissed-v1";
const ENABLED_KEY = "vitrine_banner_enabled";

const GlobalVitrineBanner = () => {
  const location = useLocation();
  const [enabled, setEnabled] = useState(true); // default ligado
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      const en = localStorage.getItem(ENABLED_KEY);
      // default true: só desliga se explicitamente "false"
      setEnabled(en !== "false");
      setDismissed(localStorage.getItem(DISMISS_KEY) === "1");
    } catch {}
  }, []);

  if (isAndroidTWA()) return null;
  if (location.pathname === "/vitrine") return null;
  if (!enabled || dismissed) return null;

  const handleClose = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {}
  };

  return (
    <Link
      to="/vitrine"
      className="block w-full group animate-fade-in"
      title="✨ Vitrine — todas as ferramentas do IA Palpite Guru"
    >
      <div className="w-full bg-gradient-to-r from-zinc-950 via-black to-zinc-950 border-b-2 border-yellow-500/60 overflow-hidden relative">
        <div className="container mx-auto px-4 py-4 md:py-5">
          <div className="flex items-center justify-between gap-3 md:gap-6">
            {/* Left side - Badge */}
            <div className="hidden sm:flex items-center gap-2 bg-yellow-500/10 rounded-lg px-3 py-2 border border-yellow-500/30">
              <Star className="h-5 w-5 text-yellow-400 flex-shrink-0 fill-yellow-400" />
              <div className="text-xs md:text-sm">
                <span className="text-yellow-400 font-semibold">Vitrine</span>
                <br />
                <span className="text-white/90">Tour Completo</span>
              </div>
            </div>

            {/* Center - Main Text */}
            <div className="flex-1 text-center">
              <div className="flex flex-col md:flex-row items-center justify-center gap-1 md:gap-3">
                <div className="flex items-center gap-2">
                  <div className="bg-yellow-400 rounded-full p-1.5">
                    <Sparkles className="text-black h-4 w-4 md:h-5 md:w-5" />
                  </div>
                  <span className="text-yellow-400 font-bold text-base md:text-xl lg:text-2xl">
                    Vitrine
                  </span>
                </div>
                <div className="text-white">
                  <span className="text-sm md:text-xl lg:text-2xl">Descubra todas as </span>
                  <span className="font-bold text-sm md:text-xl lg:text-2xl">ferramentas</span>
                  <span className="text-yellow-300 font-extrabold text-base md:text-2xl lg:text-3xl ml-2">
                    IA Palpite Guru!
                  </span>
                </div>
              </div>
              <p className="text-[11px] md:text-xs text-zinc-400 mt-1 hidden md:block">
                Métodos · IA · Estatísticas · Resultados ao vivo · Loja
              </p>
            </div>

            {/* Right side - CTA + close */}
            <div className="flex items-center gap-2 md:gap-3">
              <div className="bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-black font-bold text-sm md:text-base px-4 md:px-6 py-2.5 md:py-3 rounded-lg shadow-lg group-hover:shadow-yellow-400/30 transition-all duration-300 flex items-center gap-2 whitespace-nowrap animate-pulse-subtle">
                <span>Explorar Agora</span>
                <ArrowRight className="h-4 w-4 md:h-5 md:w-5" />
              </div>
              <button
                onClick={handleClose}
                aria-label="Fechar banner"
                className="p-1 text-zinc-400 hover:text-white transition-colors shrink-0"
              >
                <X className="h-4 w-4 md:h-5 md:w-5" />
              </button>
            </div>
          </div>

          {/* Mobile badge */}
          <div className="flex sm:hidden items-center justify-center gap-2 mt-2 text-xs text-zinc-400">
            <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
            <span>Tour completo · Métodos · IA · Estatísticas</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default GlobalVitrineBanner;
